import React, {memo, useMemo, useState} from 'react'
import {
  Handle,
  Position,
  type NodeProps,
  useNodes,
  type Node,
  useReactFlow,
  type Edge,
} from 'reactflow'
import {toAbsoluteUrl} from '../../../../../../../_metronic/helpers'
import {useI18n} from '../../../../../../Utils/_helpers/useI18n'
import {LOGIC} from '../../../../../../Utils/Constant/FlowConstants'
import {type DataNode, type MetaDataWithAddTag, type TagOption} from '../../FlowTypes'
import {useFlowNodeDelete} from '../hooks/useFlowNodeDelete'

export type AddTagNodeProps = NodeProps<DataNode<MetaDataWithAddTag>>

type DeletableDataNode = DataNode<MetaDataWithAddTag> & {deletable?: boolean}

const AddTagNode: React.FC<AddTagNodeProps> = ({id, selected}) => {
  const {t} = useI18n()
  const {setNodes, setEdges, getEdges} = useReactFlow()
  const nodes = useNodes()
  const edges = getEdges()
  const [hovered, setHovered] = useState(false)

  const item = useMemo(
    () => LOGIC.find((logicItem) => logicItem.type === 'Add_tag') ?? LOGIC[0],
    []
  )

  const node = useMemo(
    () => nodes.find((n) => n.id === id) as Node<DeletableDataNode> | undefined,
    [nodes, id]
  )

  const {handleDelete} = useFlowNodeDelete({
    nodes,
    edges,
    setNodes,
    setEdges,
    pushToUndoStack: undefined,
    isUndoRedoOperation: undefined,
  })

  const title = t('FLOW.NODE.ADD_TAG_DESC')

  const tagPreview = useMemo(() => {
    const tags: TagOption[] | undefined = node?.data?.metaData?.tags
    if (!tags || tags.length === 0) {
      return t('COMMON.NO_TAGS_FOUND')
    }
    const maxToShow = 3
    const labels = tags.slice(0, maxToShow).map((tag) => tag.label)
    const remaining = tags.length - labels.length
    return remaining > 0 ? `${labels.join(', ')} +${remaining}` : labels.join(', ')
  }, [node?.data?.metaData?.tags, t])

  return (
    <div
      className={`p-3 rounded bg-white text-center position-relative shadow-sm${
        selected ? ' border border-2 border-primary' : ''
      } nopan${hovered ? ' node-hover' : ''}`}
      style={{width: 250, minHeight: 90}}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      title={title}
    >
      {node?.data?.deletable !== false && (
        <button
          className='btn btn-link p-0 position-absolute end-0 top-0 m-2'
          style={{zIndex: 10}}
          title='Delete Node'
          onClick={(e) => {
            if (node) {
              handleDelete(id, node, e)
            }
          }}
        >
          <i className='bi bi-trash text-danger fs-5'></i>
        </button>
      )}

      <Handle
        type='target'
        position={Position.Top}
        id='addtag-top'
        style={{background: 'var(--bs-gray-600)'}}
      />

      <div className='d-flex flex-column gap-2'>
        <div className='d-flex align-items-center gap-2'>
          <div
            className='automation-icon-container'
            style={{
              backgroundColor: item.bgColor,
            }}
          >
            <img src={toAbsoluteUrl(item.svg || '')} alt={item.icon} className='automation-icon' />
          </div>

          <span
            className='text-truncate fw-medium'
            style={{maxWidth: '140px'}}
            title={title}
          >
            {t(`FLOW.NODE.${item.type.toUpperCase()}`)}
          </span>
        </div>

        <div className='d-flex flex-column'>
         {
          tagPreview.length > 0 ? (
            <div className='d-flex flex-column'>
              <span>Preview Tags:</span>
            <p
              className='mb-0 small text-muted text-truncate'
              style={{
                maxWidth: 220,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
              title={tagPreview}
            >
              {tagPreview}
            </p>
            </div>
          ) : (
            <div className='d-flex flex-column'>
              <p className='mb-0 small text-muted text-truncate' style={{maxWidth: 220}}>
                {t('COMMON.NO_TAGS_FOUND')}
              </p>
            </div>
          )
        }
        </div>
      </div>

      <Handle
        type='source'
        position={Position.Bottom}
        id='addtag-bottom'
        className='bg-primary'
      />
    </div>
  )
}

export default memo(AddTagNode)

